/**
 * CelebrationOverlay - ADHD-friendly reward animation
 * F078: Shows confetti particles + message on task completion milestones.
 * Auto-dismisses. Canvas-based particles for smooth performance.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { rewardService } from '../../services/ai/rewardService';
import type { CelebrationEvent, CelebrationLevel } from '../../services/ai/rewardService';
import './CelebrationOverlay.css';

const PARTICLE_COUNT: Record<CelebrationLevel, number> = {
  micro: 0,
  small: 30,
  medium: 60,
  big: 120,
};

const DURATION: Record<CelebrationLevel, number> = {
  micro: 0,
  small: 2000,
  medium: 3000,
  big: 4500,
};

const COLORS = [
  '#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff',
  '#ff9ff3', '#feca57', '#48dbfb', '#ff6348',
  '#7bed9f', '#e056fd', '#45aaf2', '#fc5c65',
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  life: number;
  decay: number;
  shape: 'rect' | 'circle';
}

export default function CelebrationOverlay() {
  const [event, setEvent] = useState<CelebrationEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);

  // Subscribe to celebration events
  useEffect(() => {
    const unsub = rewardService.onCelebration((e) => {
      if (e.level === 'micro') return; // no overlay for micro
      setEvent(e);
      setVisible(true);
    });
    return unsub;
  }, []);

  // Run animation when visible
  useEffect(() => {
    if (!visible || !event) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Size canvas to viewport
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create particles
    const count = PARTICLE_COUNT[event.level];
    const particles: Particle[] = [];

    for (let i = 0; i < count; i++) {
      particles.push({
        x: canvas.width * 0.5 + (Math.random() - 0.5) * canvas.width * 0.6,
        y: -20 - Math.random() * 100,
        vx: (Math.random() - 0.5) * 8,
        vy: Math.random() * 3 + 2,
        size: Math.random() * 8 + 4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        life: 1,
        decay: 0.003 + Math.random() * 0.005,
        shape: Math.random() > 0.5 ? 'rect' : 'circle',
      });
    }
    particlesRef.current = particles;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let alive = 0;
      for (const p of particles) {
        if (p.life <= 0) continue;
        alive++;

        p.x += p.vx;
        p.vy += 0.1; // gravity
        p.y += p.vy;
        p.vx *= 0.99; // air resistance
        p.rotation += p.rotationSpeed;
        p.life -= p.decay;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.globalAlpha = Math.max(0, p.life);
        ctx.fillStyle = p.color;

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (alive > 0) {
        animRef.current = requestAnimationFrame(animate);
      }
    };

    animRef.current = requestAnimationFrame(animate);

    // Auto-dismiss
    const duration = DURATION[event.level];
    const timer = setTimeout(() => {
      setVisible(false);
      setEvent(null);
    }, duration);

    return () => {
      cancelAnimationFrame(animRef.current);
      clearTimeout(timer);
    };
  }, [visible, event]);

  const dismiss = useCallback(() => {
    setVisible(false);
    setEvent(null);
  }, []);

  if (!visible || !event) return null;

  return (
    <div className="celebration-overlay" onClick={dismiss}>
      <canvas ref={canvasRef} className="celebration-canvas" />
      <div className={`celebration-message celebration-${event.level}`}>
        <span className="celebration-emoji">{event.emoji}</span>
        <span className="celebration-text">{event.message}</span>
      </div>
    </div>
  );
}
